import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Cpu, LineChart, FileBadge, Waves, CreditCard, ArrowUpRight } from "lucide-react";
import DecodeText from "./DecodeText";
import Hud from "./HudOverlay";

const services = [
  {
    id: "incorporation",
    icon: FileBadge,
    title: "Incorporation & Licensing",
    line: "UAE, Saint Lucia, Seychelles, Mauritius — bank-ready in 7–10 days.",
    to: "/incorporation",
  },
  {
    id: "financial",
    icon: TrendingUp,
    title: "Advisory & Consulting",
    line: "End-to-end launch guidance for brokerages and prop firms.",
    to: "/brokerage-launch",
  },
  {
    id: "technology",
    icon: Cpu,
    title: "Technology Set-Ups",
    line: "Providers selected, negotiated and integrated for you.",
    to: "/platform-setup",
  },
  {
    id: "trading",
    icon: LineChart,
    title: "Trading Platforms",
    line: "Match-Trader, cTrader, MT4/MT5 & EXO — fitted to your model.",
    to: "/trading-platforms",
  },
  {
    id: "liquidity",
    icon: Waves,
    title: "Liquidity & Bridges",
    line: "Centroid & FX-EDGE — liquidity wired right.",
    to: "/lp-bridge",
  },
  {
    id: "payments",
    icon: CreditCard,
    title: "Payments & Banking",
    line: "Corporate bank accounts, card payment providers and crypto rails — opened and connected.",
    to: "/payments",
  },
];

const ServiceCard = ({ s, index, onOpen }) => {
  const Icon = s.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 28, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={{ y: -5 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group lux-card trace-border relative overflow-hidden rounded-2xl p-5 cursor-pointer"
      data-testid={`service-${s.id}`}
    >
      <Hud />
      <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(147,51,234,0.18) 0%, transparent 70%)" }} />

      <div className="relative flex items-center justify-between">
        <div className="w-9 h-9 rounded-lg border border-purple-500/30 bg-purple-600/10 flex items-center justify-center text-purple-300 icon-glow">
          <Icon size={18} strokeWidth={1.4} />
        </div>
        <ArrowUpRight
          size={15}
          strokeWidth={1.6}
          className="text-purple-300/70 group-hover:text-purple-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
        />
      </div>

      <h3 className="relative font-display text-lg sm:text-xl mt-4 text-zinc-100 leading-tight group-hover:text-purple-100 transition-colors">
        {s.title}
      </h3>
      <p className="relative mt-1.5 text-[13px] text-zinc-400 leading-relaxed">{s.line}</p>
    </motion.div>
  );
};

const Services = () => {
  const navigate = useNavigate();

  return (
    <section
      id="services"
      className="relative py-10 sm:py-14 lg:py-20 overflow-hidden"
      data-testid="services-section"
    >
      <div className="absolute inset-0 bg-[#050b1c]/85" />
      <div className="absolute left-0 top-1/2 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(109,40,217,0.22) 0%, transparent 70%)" }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 sm:gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 text-[11px] tracking-[0.5em] text-purple-400 uppercase">
              <span className="w-8 h-px bg-purple-500/60 neon-dash" />
              Services
            </div>
            <h2 className="font-display mt-6 text-3xl sm:text-4xl lg:text-5xl text-zinc-100 leading-[1.05] tracking-tight max-w-3xl">
              Everything a launch needs.
              <br />
              <em className="text-purple-400 not-italic text-glow"><DecodeText text="One team runs it" /></em>
              <span className="text-zinc-500">.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.95, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-zinc-500 max-w-sm text-sm leading-relaxed"
          >
            Tap any service to see how we run it.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((s, i) => (
            <ServiceCard s={s} index={i} key={s.id} onOpen={() => navigate(s.to)} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
